import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Sparkles, GitBranch, Table2, Target, AlertTriangle, Link2,
  Download, FileText, Image, ArrowLeftRight, Eye
} from 'lucide-react';
import { FishboneDiagram } from '../components/fishbone/FishboneDiagram';
import { FishboneTableView } from '../components/fishbone/FishboneTableView';
import { AiSuggestionsPanel } from '../components/fishbone/AiSuggestionsPanel';
import {
  AddCauseDialog, AddSubCauseDialog, AddCategoryDialog,
  CauseDetailDialog, EvidenceLinkDialog, CompareDialog, PatternsDialog
} from '../components/fishbone/FishboneDialogs';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_OPTS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  { value: 'review', label: 'Under Review', color: 'bg-amber-100 text-amber-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
];

export default function Fishbone() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'iic' || user?.role === 'analyst';

  const [diagram, setDiagram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completeness, setCompleteness] = useState(null);
  const [viewMode, setViewMode] = useState('diagram');

  // Dialogs
  const [addCauseDialog, setAddCauseDialog] = useState(null);
  const [causeText, setCauseText] = useState('');
  const [addSubDialog, setAddSubDialog] = useState(null);
  const [subText, setSubText] = useState('');
  const [causeDetailDialog, setCauseDetailDialog] = useState(null);
  const [addCategoryDialog, setAddCategoryDialog] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('#6b7280');

  // AI
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTargetCat, setAiTargetCat] = useState('');

  // Evidence linking
  const [evidenceDialog, setEvidenceDialog] = useState(null);
  const [evidenceList, setEvidenceList] = useState([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);

  // Compare
  const [showCompare, setShowCompare] = useState(false);
  const [compareInvestigations, setCompareInvestigations] = useState([]);
  const [compareId, setCompareId] = useState('');
  const [compareResult, setCompareResult] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // Patterns
  const [showPatterns, setShowPatterns] = useState(false);
  const [patterns, setPatterns] = useState(null);

  // Drag & expanded
  const [dragItem, setDragItem] = useState(null);
  const [expandedCats, setExpandedCats] = useState({});

  const loadDiagram = useCallback(async () => {
    try {
      const [diagRes, compRes] = await Promise.all([
        axios.get(`${API}/api/fishbone/investigation/${id}`, { withCredentials: true }),
        axios.get(`${API}/api/fishbone/investigation/${id}/completeness`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setDiagram(diagRes.data);
      setCompleteness(compRes.data);
      const exp = {};
      (diagRes.data?.categories || []).forEach(c => { exp[c.id] = true; });
      setExpandedCats(exp);
    } catch (e) { console.error('Failed to load diagram:', e); } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadDiagram(); }, [loadDiagram]);

  // --- Handlers ---
  const handleAddCause = async () => {
    if (!causeText.trim() || !addCauseDialog) return;
    await axios.post(`${API}/api/fishbone/investigation/${id}/category/${addCauseDialog.catId}/cause`, { text: causeText.trim() }, { withCredentials: true });
    setCauseText(''); setAddCauseDialog(null); loadDiagram();
  };
  const handleAddSubCause = async () => {
    if (!subText.trim() || !addSubDialog) return;
    await axios.post(`${API}/api/fishbone/investigation/${id}/cause/${addSubDialog.causeId}/sub-cause`, { text: subText.trim() }, { withCredentials: true });
    setSubText(''); setAddSubDialog(null); loadDiagram();
  };
  const handleDeleteCause = async (catId, causeId) => {
    if (!window.confirm('Delete this cause and all its sub-causes?')) return;
    await axios.delete(`${API}/api/fishbone/investigation/${id}/category/${catId}/cause/${causeId}`, { withCredentials: true });
    setCauseDetailDialog(null); loadDiagram();
  };
  const handleDeleteSubCause = async (causeId, subId) => {
    await axios.delete(`${API}/api/fishbone/investigation/${id}/cause/${causeId}/sub-cause/${subId}`, { withCredentials: true });
    loadDiagram();
  };
  const handleToggleRootCause = async (causeId, current) => {
    await axios.put(`${API}/api/fishbone/investigation/${id}/cause/${causeId}`, { is_root_cause: !current }, { withCredentials: true });
    loadDiagram();
  };
  const handleToggleContributing = async (causeId, current) => {
    await axios.put(`${API}/api/fishbone/investigation/${id}/cause/${causeId}`, { is_contributing_factor: !current }, { withCredentials: true });
    loadDiagram();
  };
  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    await axios.post(`${API}/api/fishbone/investigation/${id}/category`, { name: newCatName.trim(), color: newCatColor }, { withCredentials: true });
    setNewCatName(''); setAddCategoryDialog(false); loadDiagram();
  };
  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('Delete this category and all its causes?')) return;
    await axios.delete(`${API}/api/fishbone/investigation/${id}/category/${catId}`, { withCredentials: true });
    loadDiagram();
  };
  const handleAiSuggest = async (categoryName = '') => {
    setAiLoading(true); setAiTargetCat(categoryName);
    try {
      const res = await axios.post(`${API}/api/fishbone/investigation/${id}/ai-suggest`, { category_name: categoryName }, { withCredentials: true });
      setAiSuggestions(res.data.suggestions);
    } catch (e) { alert(e.response?.data?.detail || 'AI suggestion failed'); }
    finally { setAiLoading(false); }
  };
  const handleAcceptSuggestion = async (suggestion) => {
    const cat = (diagram?.categories || []).find(c => c.name.toLowerCase() === suggestion.category.toLowerCase());
    if (!cat) { alert(`Category "${suggestion.category}" not found`); return; }
    const res = await axios.post(`${API}/api/fishbone/investigation/${id}/category/${cat.id}/cause`, { text: suggestion.cause }, { withCredentials: true });
    if (suggestion.sub_causes?.length > 0 && res.data.cause?.id) {
      for (const sub of suggestion.sub_causes) {
        await axios.post(`${API}/api/fishbone/investigation/${id}/cause/${res.data.cause.id}/sub-cause`, { text: sub }, { withCredentials: true });
      }
    }
    setAiSuggestions(prev => prev.filter(s => s !== suggestion));
    loadDiagram();
  };
  const handleAutoClassify = async (text) => {
    const res = await axios.post(`${API}/api/fishbone/investigation/${id}/auto-classify`, { text }, { withCredentials: true });
    return res.data;
  };
  const handleExport = async (format) => {
    const res = await fetch(`${API}/api/fishbone/investigation/${id}/export/${format}`, { credentials: 'include' });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Fishbone_${diagram?.title || 'analysis'}.${format === 'docx' ? 'docx' : format === 'png' ? 'png' : 'pdf'}`;
      a.click(); URL.revokeObjectURL(url);
    }
  };
  const openEvidenceDialog = async (causeId, currentIds) => {
    setEvidenceDialog({ causeId, currentIds: currentIds || [] });
    setEvidenceLoading(true);
    try { const res = await axios.get(`${API}/api/fishbone/investigation/${id}/evidence-list`, { withCredentials: true }); setEvidenceList(res.data); }
    catch (e) { console.error('Failed to load evidence:', e); setEvidenceList([]); } finally { setEvidenceLoading(false); }
  };
  const toggleEvidence = async (evidenceId) => {
    if (!evidenceDialog) return;
    const current = evidenceDialog.currentIds || [];
    const newIds = current.includes(evidenceId) ? current.filter(e => e !== evidenceId) : [...current, evidenceId];
    await axios.put(`${API}/api/fishbone/investigation/${id}/cause/${evidenceDialog.causeId}`, { evidence_ids: newIds }, { withCredentials: true });
    setEvidenceDialog({ ...evidenceDialog, currentIds: newIds }); loadDiagram();
  };
  const handleDragStart = (type, catId, itemId) => { setDragItem({ type, catId, itemId }); };
  const handleDrop = async (targetCatId, targetIdx) => {
    if (!dragItem) return;
    if (dragItem.type === 'category') {
      const cats = diagram.categories.map(c => c.id);
      const fromIdx = cats.indexOf(dragItem.itemId); if (fromIdx === -1) return;
      cats.splice(fromIdx, 1); cats.splice(targetIdx, 0, dragItem.itemId);
      await axios.post(`${API}/api/fishbone/investigation/${id}/reorder`, { type: 'categories', order: cats }, { withCredentials: true });
    } else if (dragItem.type === 'cause') {
      const cat = diagram.categories.find(c => c.id === targetCatId); if (!cat) return;
      const causeIds = cat.causes.map(c => c.id);
      const fromIdx = causeIds.indexOf(dragItem.itemId); if (fromIdx === -1) return;
      causeIds.splice(fromIdx, 1); causeIds.splice(targetIdx, 0, dragItem.itemId);
      await axios.post(`${API}/api/fishbone/investigation/${id}/reorder`, { type: 'causes', category_id: targetCatId, order: causeIds }, { withCredentials: true });
    }
    setDragItem(null); loadDiagram();
  };
  const handleUpdateStatus = async (status) => { await axios.put(`${API}/api/fishbone/investigation/${id}`, { status }, { withCredentials: true }); loadDiagram(); };
  const handleCauseClick = (cat, cause) => { setCauseDetailDialog({ cat, cause }); };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;
  if (!diagram) return <div className="text-center py-12 text-gray-500">Fishbone diagram not found.</div>;

  const currentStatus = STATUS_OPTS.find(s => s.value === diagram.status) || STATUS_OPTS[0];

  return (
    <div className="max-w-7xl mx-auto" data-testid="fishbone-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/investigations/${id}`)} data-testid="fishbone-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Fishbone Analysis</h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">{diagram.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={diagram.status} onChange={(e) => handleUpdateStatus(e.target.value)} disabled={!canEdit}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border-0 cursor-pointer ${currentStatus.color}`} data-testid="fishbone-status-select">
            {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setViewMode('diagram')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'diagram' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`} data-testid="view-diagram-btn">
              <GitBranch className="w-3.5 h-3.5 inline mr-1" /> Diagram
            </button>
            <button onClick={() => setViewMode('table')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'table' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`} data-testid="view-table-btn">
              <Table2 className="w-3.5 h-3.5 inline mr-1" /> Table
            </button>
          </div>
          <div className="relative group">
            <Button variant="outline" size="sm" className="border-slate-300 text-slate-700" data-testid="export-menu-btn"><Download className="w-3.5 h-3.5 mr-1" /> Export</Button>
            <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 hidden group-hover:block w-40">
              <button onClick={() => handleExport('pdf')} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="export-pdf-btn"><FileText className="w-3.5 h-3.5 text-red-500" /> PDF Report</button>
              <button onClick={() => handleExport('docx')} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="export-docx-btn"><FileText className="w-3.5 h-3.5 text-blue-500" /> Word Document</button>
              <button onClick={() => handleExport('png')} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="export-png-btn"><Image className="w-3.5 h-3.5 text-green-500" /> PNG Image</button>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => { setShowCompare(true); axios.get(`${API}/api/fishbone/all-investigations`, { withCredentials: true }).then(r => setCompareInvestigations(r.data.filter(d => d.investigation_id !== id))); }}
            className="border-slate-300 text-slate-700" data-testid="compare-btn"><ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Compare</Button>
          <Button variant="outline" size="sm" onClick={() => { setShowPatterns(true); axios.get(`${API}/api/fishbone/patterns`, { withCredentials: true }).then(r => setPatterns(r.data)); }}
            className="border-slate-300 text-slate-700" data-testid="patterns-btn"><Eye className="w-3.5 h-3.5 mr-1" /> Patterns</Button>
          {canEdit && (
            <Button size="sm" onClick={() => handleAiSuggest('')} disabled={aiLoading} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="ai-suggest-all-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {aiLoading ? 'Analyzing...' : 'AI Suggest'}
            </Button>
          )}
        </div>
      </div>

      {/* Completeness */}
      {completeness && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mb-4 bg-white rounded-lg border border-slate-200 px-4 py-2.5" data-testid="completeness-stats">
          {[
            { label: 'Categories', value: completeness.total_categories, icon: GitBranch, color: 'text-slate-600' },
            { label: 'Causes', value: completeness.total_causes, icon: Target, color: 'text-slate-600' },
            { label: 'Root', value: completeness.root_causes, icon: AlertTriangle, color: 'text-red-600' },
            { label: 'Contributing', value: completeness.contributing_factors, icon: AlertTriangle, color: 'text-amber-600' },
            { label: 'Evidence', value: completeness.total_evidence_links, icon: Link2, color: 'text-blue-600' },
          ].map((stat) => (
            <span key={stat.label} className="flex items-center gap-1.5 text-xs">
              <stat.icon className={`w-3.5 h-3.5 ${stat.color}`} />
              <span className="font-bold text-slate-800" style={{ fontFamily: 'Outfit' }}>{stat.value}</span>
              <span className="text-slate-500">{stat.label}</span>
            </span>
          ))}
          {completeness.alerts?.length > 0 && (
            <span className="flex items-center gap-1.5 text-xs text-amber-600 ml-auto">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span className="font-medium">{completeness.alerts.length} gap{completeness.alerts.length > 1 ? 's' : ''}</span>
            </span>
          )}
        </div>
      )}

      {/* Diagram View */}
      {viewMode === 'diagram' && (
        <div className="mb-6">
          <FishboneDiagram diagram={diagram} onCauseClick={handleCauseClick} />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-3" data-testid="diagram-cause-summary">
            {(diagram.categories || []).map(cat => (
              <div key={cat.id} className="bg-white rounded-lg border border-slate-200 p-2.5">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                  <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wider truncate">{cat.name}</span>
                </div>
                {(cat.causes || []).length === 0 ? (
                  <span className="text-[10px] text-slate-300 italic">No causes</span>
                ) : (
                  <ul className="space-y-0.5">
                    {(cat.causes || []).map(cause => (
                      <li key={cause.id} className="flex items-start gap-1 cursor-pointer hover:bg-slate-50 rounded px-0.5 -mx-0.5" onClick={() => handleCauseClick(cat, cause)}>
                        <span className={`shrink-0 mt-[5px] rounded-full ${cause.is_root_cause ? 'w-1.5 h-1.5 bg-red-500' : cause.is_contributing_factor ? 'w-1.5 h-1.5 bg-amber-500' : 'w-1 h-1 bg-slate-400'}`}
                          style={!cause.is_root_cause && !cause.is_contributing_factor ? { backgroundColor: cat.color, opacity: 0.6 } : {}} />
                        <span className={`text-[10px] leading-tight ${cause.is_root_cause ? 'text-red-700 font-medium' : cause.is_contributing_factor ? 'text-amber-700 font-medium' : 'text-slate-600'}`}>{cause.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <FishboneTableView diagram={diagram} canEdit={canEdit} expandedCats={expandedCats} setExpandedCats={setExpandedCats}
          handleDragStart={handleDragStart} handleDrop={handleDrop} handleAiSuggest={handleAiSuggest} aiLoading={aiLoading}
          setAddCauseDialog={setAddCauseDialog} setCauseText={setCauseText} handleDeleteCategory={handleDeleteCategory}
          handleToggleRootCause={handleToggleRootCause} handleToggleContributing={handleToggleContributing}
          openEvidenceDialog={openEvidenceDialog} setAddSubDialog={setAddSubDialog} setSubText={setSubText}
          handleDeleteCause={handleDeleteCause} handleDeleteSubCause={handleDeleteSubCause} setAddCategoryDialog={setAddCategoryDialog} />
      )}

      {/* AI Suggestions */}
      <AiSuggestionsPanel suggestions={aiSuggestions} targetCat={aiTargetCat} onAccept={handleAcceptSuggestion} onDismiss={() => setAiSuggestions(null)} />

      {/* Dialogs */}
      <AddCauseDialog open={!!addCauseDialog} dialog={addCauseDialog} causeText={causeText} setCauseText={setCauseText} onAdd={handleAddCause} onAutoClassify={handleAutoClassify} onClose={() => setAddCauseDialog(null)} />
      <AddSubCauseDialog open={!!addSubDialog} dialog={addSubDialog} subText={subText} setSubText={setSubText} onAdd={handleAddSubCause} onClose={() => setAddSubDialog(null)} />
      <AddCategoryDialog open={addCategoryDialog} name={newCatName} setName={setNewCatName} color={newCatColor} setColor={setNewCatColor} onAdd={handleAddCategory} onClose={setAddCategoryDialog} />
      <CauseDetailDialog open={!!causeDetailDialog} dialog={causeDetailDialog} canEdit={canEdit} onToggleRoot={handleToggleRootCause} onToggleContributing={handleToggleContributing} onOpenEvidence={openEvidenceDialog} onDeleteCause={handleDeleteCause} onClose={() => setCauseDetailDialog(null)} />
      <EvidenceLinkDialog open={!!evidenceDialog} dialog={evidenceDialog} evidenceList={evidenceList} evidenceLoading={evidenceLoading} onToggle={toggleEvidence} onClose={() => setEvidenceDialog(null)} />
      <CompareDialog open={showCompare} onClose={setShowCompare} investigations={compareInvestigations} compareId={compareId} setCompareId={setCompareId} onCompare={async () => {
        if (!compareId) return; setCompareLoading(true);
        try { const res = await axios.post(`${API}/api/fishbone/compare`, { investigation_id_1: id, investigation_id_2: compareId }, { withCredentials: true }); setCompareResult(res.data); }
        catch (e) { alert(e.response?.data?.detail || 'Compare failed'); } finally { setCompareLoading(false); }
      }} compareLoading={compareLoading} compareResult={compareResult} />
      <PatternsDialog open={showPatterns} onClose={setShowPatterns} patterns={patterns} />
    </div>
  );
}
